(() => {
  'use strict';
  if (window.BroekCartUX?.version === 'qa-06sep-groups-r3') return;
  const DRAWER = '#minicart_wrapper';
  const q = (selector, root = document) => root?.querySelector(selector);
  const qa = (selector, root = document) => Array.from(root?.querySelectorAll(selector) || []);
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  let busy = false, uncertain = false, needsReview = false;
  let refreshing = null, opener = null, lastCart = null, revision = 0, reviewCheck = null;
  const validInt = value => Number.isSafeInteger(Number(value)) && Number(value) >= 0;
  function properties(value) {
    const entries = Array.isArray(value) ? value : Object.entries(value || {});
    return Object.fromEntries(entries.filter(pair => Array.isArray(pair) && pair.length === 2 && pair[1] != null && pair[1] !== '').map(([key, value]) => [String(key), String(value)]));
  }
  const sortedProperties = (line, publicOnly = false) => Object.entries(properties(line.properties)).filter(([key]) => !publicOnly || !key.startsWith('_')).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  const planId = line => String(line.selling_plan_id || line.selling_plan_allocation?.selling_plan?.id || 0);
  function isolated(line) {
    return !!(line.instructions?.can_update_quantity === false || line.instructions?.can_remove === false || line.parent_relationship || Number(line.components_count) > 0 || line.item_components?.length || line.has_components || Object.keys(properties(line.properties)).some(key => /bundle|parent|child|subscription|selling_plan/i.test(key)));
  }
  const groupId = line => JSON.stringify([String(line.variant_id || line.id), planId(line), sortedProperties(line, true), isolated(line) ? String(line.key) : '']);
  const fullId = line => JSON.stringify([String(line.variant_id || line.id), planId(line), sortedProperties(line)]);
  const totalQuantity = lines => lines.reduce((sum, line) => sum + Number(line.quantity), 0);
  const packTotals = lines => {
    const result = new Map();
    lines.forEach(line => result.set(fullId(line), (result.get(fullId(line)) || 0) + Number(line.quantity)));
    return result;
  };
  const signature = lines => JSON.stringify(Array.from(packTotals(lines)).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0));
  const linesFor = (cart, id) => (cart.items || []).filter(line => groupId(line) === id);
  function rowData(row) {
    return qa('script.broek-cart-line-data', row).map(node => {
      try { const line = JSON.parse(node.textContent); return line && line.key && validInt(line.quantity) && validInt(line.final_line_price) ? line : null; }
      catch (_) { return null; }
    }).filter(Boolean);
  }
  function money(cents, format) {
    if (typeof window.Shopify?.formatMoney === 'function') return window.Shopify.formatMoney(cents, format);
    const formats = {
      amount: [2, ',', '.'], amount_no_decimals: [0, ',', '.'], amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','], amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','], amount_with_apostrophe_separator: [2, "'", '.']
    };
    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
      const [decimals, thousands, decimal] = formats[name] || formats.amount;
      const parts = (Number(cents) / 100).toFixed(decimals).split('.');
      return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (parts[1] ? decimal + parts[1] : '');
    });
  }
  function syncGroups() {
    const list = q('.items', q(DRAWER));
    if (!list) return;
    const groups = new Map();
    qa(':scope > .cart-item', list).forEach(row => {
      const lines = rowData(row);
      if (!lines.length || lines.some(line => groupId(line) !== groupId(lines[0]))) return;
      const id = groupId(lines[0]);
      const previous = groups.get(id);
      if (previous) {
        // Move metadata, never duplicate it or alter Shopify's real cart lines.
        qa('script.broek-cart-line-data', row).forEach(node => previous.row.appendChild(node));
        previous.lines.push(...lines);
        row.remove();
      } else groups.set(id, {row, lines});
    });
    groups.forEach(({row, lines}, id) => {
      const quantity = totalQuantity(lines);
      const finalTotal = lines.reduce((sum, line) => sum + Number(line.final_line_price), 0);
      const compareTotal = lines.reduce((sum, line) => sum + Math.max(Number(line.compare_at_price) || 0, Number(line.original_price) || 0) * Number(line.quantity), 0);
      const free = Math.min(quantity, lines.reduce((sum, line) => sum + Math.max(0, Math.min(Number(line.quantity), Number(line.free_quantity) || 0)), 0));
      row.dataset.broekGroupId = id;
      row.dataset.broekGroupQuantity = String(quantity);
      row.classList.add('broek-cart-group');
      const input = q('.broek-cart-qty__input', row);
      if (input) input.value = quantity;
      const minus = q('[data-broek-qty-action="minus"]', row);
      if (minus) minus.disabled = quantity <= 1;
      const price = q('[data-broek-group-total]', row), compare = q('[data-broek-group-compare]', row);
      const format = q('[data-broek-money-format]', row)?.dataset.broekMoneyFormat;
      if (price) price.innerHTML = money(finalTotal, format);
      if (compare) { compare.innerHTML = money(compareTotal, format); compare.hidden = compareTotal <= finalTotal; }
      const freeText = q('[data-broek-group-free]', row);
      if (freeText) {
        freeText.hidden = free === 0;
        freeText.textContent = free ? `${quantity > free ? `${quantity - free} paid + ` : ''}${free} FREE` : '';
      }
      const edit = q('[data-broek-edit-toggle]', row);
      if (edit) edit.hidden = lines.some(isolated) || planId(lines[0]) !== '0';
      if (lines.some(line => line.instructions?.can_update_quantity === false)) q('.broek-cart-qty', row)?.setAttribute('hidden', '');
      if (lines.some(line => line.instructions?.can_remove === false)) q('mini-cart-remove-button', row)?.setAttribute('hidden', '');
    });
  }
  function invalidateRefresh() { revision += 1; refreshing = null; }
  function counts(cart) {
    if (!validInt(cart?.item_count)) return;
    lastCart = cart;
    if (Array.isArray(cart.items) && reviewCheck) { needsReview = reviewCheck(cart); if (!needsReview) reviewCheck = null; }
    qa('.cart-count').forEach(el => { el.textContent = el.classList.contains('cart-count-drawer') ? `(${cart.item_count})` : String(cart.item_count > 100 ? '~' : cart.item_count); });
    const header = q('header-total-price');
    if (typeof header?.updateTotal === 'function') header.updateTotal(cart);
  }
  class BroekCartState extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        const state = {item_count:Number(this.dataset.count), total_price:Number(this.dataset.total), items_subtotal_price:Number(this.dataset.subtotal)};
        if (!lastCart || lastCart.item_count !== state.item_count || lastCart.total_price !== state.total_price) counts(state);
        syncGroups();
        setBusy(busy);
        document.dispatchEvent(new CustomEvent('broek:cart-dom-synced'));
      });
    }
  }
  if (!customElements.get('broek-cart-state')) customElements.define('broek-cart-state', BroekCartState);
  function status(message, retry = false) {
    const box = q('[data-broek-cart-status]', q(DRAWER));
    if (!box) return;
    box.replaceChildren();
    if (message) box.append(document.createTextNode(message));
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.broekCartRetry = ''; button.textContent = 'Refresh cart';
      box.append(button);
    }
  }
  function setBusy(value) {
    busy = value;
    const drawer = q(DRAWER);
    if (!drawer) return;
    drawer.toggleAttribute('data-broek-busy', value);
    drawer.setAttribute('aria-busy', String(value));
    qa('[name="checkout"]', drawer).forEach(button => {
      const terms = q('.conditions_form_minicart', drawer);
      button.disabled = value || uncertain || needsReview || !!(terms && !terms.checked);
    });
  }
  function replacePart(dst, src, selector) {
    const old = q(selector, dst), next = q(selector, src);
    if (old && next) old.replaceWith(document.importNode(next, true));
  }
  function render(html) {
    const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const src = q('#minicart-form', parsed), dst = q('#minicart-form', q(DRAWER));
    if (!src || !dst) throw new Error('Cart updated. Refresh to see the latest totals.');
    const scrollTop = q('[data-broek-cart-scroll]', dst)?.scrollTop || 0;
    const active = document.activeElement, activeRow = active?.closest?.('[data-broek-line-key]');
    const activeGroup = activeRow?.dataset.broekGroupId, activeKey = activeRow?.dataset.broekLineKey;
    const action = active?.dataset?.broekQtyAction;
    const items = q('.items', dst), nextItems = q('.items', src);
    const recommendation = q('.cart-recommend', dst), nextRecommendation = q('.cart-recommend', src);
    const sameRecommendation = !!recommendation === !!nextRecommendation && recommendation?.dataset.url === nextRecommendation?.dataset.url;
    const parts = ['[data-broek-cart-totals]', '.broek-offer-progress', '[data-broek-shipping-progress]', '.cart-discount__codes', 'broek-cart-state'];
    const sameParts = parts.every(selector => !!q(selector, dst) === !!q(selector, src));
    if (items && nextItems && sameRecommendation && sameParts) {
      items.innerHTML = nextItems.innerHTML;
      parts.forEach(selector => replacePart(dst, src, selector));
    } else {
      dst.innerHTML = src.innerHTML;
      const drawer = q(DRAWER);
      if (typeof drawer?.cartAction === 'function') drawer.cartAction();
    }
    syncGroups();
    const scroll = q('[data-broek-cart-scroll]', dst);
    if (scroll) scroll.scrollTop = scrollTop;
    if (activeRow) {
      const row = qa('[data-broek-line-key]', dst).find(el => (activeGroup && el.dataset.broekGroupId === activeGroup) || el.dataset.broekLineKey === activeKey);
      const control = action ? q(`[data-broek-qty-action="${action}"]`, row || null) : q('[data-broek-edit-toggle]', row || null);
      if (control && !control.disabled && !control.hidden) control.focus({preventScroll:true});
      else q('.close-cart-button', q(DRAWER))?.focus({preventScroll:true});
    }
    setBusy(busy);
  }
  function sectionUrl() {
    const url = new URL(rootUrl(), location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  async function refreshFromServer() {
    if (refreshing) return refreshing;
    const requestRevision = revision;
    const request = (async () => {
      const url = sectionUrl(); url.searchParams.set('section_id', 'minicart-form');
      const response = await fetch(url.pathname + url.search, {credentials:'same-origin', cache:'no-store', headers:{Accept:'text/html'}});
      const html = await response.text();
      if (requestRevision !== revision) return lastCart;
      if (!response.ok) throw new Error('Could not refresh the cart. Please try again.');
      render(html);
      return lastCart;
    })();
    refreshing = request;
    try { return await request; } finally { if (refreshing === request) refreshing = null; }
  }
  async function currentCart() {
    const response = await fetch(rootUrl() + 'cart.js', {credentials:'same-origin', cache:'no-store', headers:{Accept:'application/json'}});
    if (!response.ok) throw new Error('Could not load your cart. Please try again.');
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !validInt(cart.item_count)) throw new Error('Could not read your cart. Please refresh.');
    return cart;
  }
  async function applyResponse(data) {
    if (!data || data.errors || Number(data.status) >= 400) throw new Error(data?.description || data?.message || 'Could not update the cart.');
    invalidateRefresh();
    if (data.item_count != null) counts(data);
    if (data.sections?.['minicart-form']) {
      try { render(data.sections['minicart-form']); } catch (_) { await refreshFromServer(); }
    } else await refreshFromServer();
    return data;
  }
  async function post(action, payload) {
    const url = sectionUrl();
    const response = await fetch(rootUrl() + `cart/${action}.js`, {
      method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json', Accept:'application/json'},
      body:JSON.stringify({...payload, sections:['minicart-form'], sections_url:url.pathname + url.search})
    });
    const data = await response.json();
    if (!response.ok || data.errors || Number(data.status) >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'Could not update your cart. Check the quantities shown.');
    return data;
  }
  async function run(operation, row) {
    if (busy) throw new Error('Please wait for the cart to finish updating.');
    invalidateRefresh(); setBusy(true); status('');
    row?.classList.add('broek-cart-line-loading');
    const state = {attempted:false, review:null};
    try {
      const data = await operation(async (action, payload) => {
        state.attempted = true; uncertain = true; return post(action, payload);
      }, state);
      await applyResponse(data);
      uncertain = false;
      if (reviewCheck && Array.isArray(data.items)) { needsReview = reviewCheck(data); if (!needsReview) reviewCheck = null; }
      if (needsReview) status('Remove the unwanted size below before checkout.');
      return data;
    } catch (error) {
      // Shopify may have committed a request whose response was lost. Read only; never replay it.
      invalidateRefresh();
      try {
        const latest = await currentCart(); counts(latest); await refreshFromServer(); uncertain = false;
        if (state.review?.(latest)) { reviewCheck = state.review; needsReview = true; }
      } catch (_) { uncertain = state.attempted || uncertain; }
      status(needsReview ? 'Size change interrupted. Remove the unwanted size below before checkout.' : error.message || 'Could not update your cart. Check the quantities shown.', uncertain);
      throw error;
    } finally {
      row?.classList.remove('broek-cart-line-loading'); setBusy(false);
    }
  }
  async function changeLine(identifier, quantity, row) {
    quantity = Number(quantity);
    if (!validInt(quantity) || !identifier) return null;
    const payload = {quantity}; payload[typeof identifier === 'number' ? 'line' : 'id'] = identifier;
    return run(write => write('change', payload), row);
  }
  async function mutateCart(action, payload, row) {
    if (!['change','update'].includes(action)) throw new Error('Unsupported cart operation.');
    return run(write => write(action, payload), row);
  }
  function rowIdentity(row) {
    const data = rowData(row);
    if (!data.length) throw new Error('Please refresh your cart before changing this item.');
    return {id:groupId(data[0]), lines:data};
  }
  async function changeGroup(row, delta, remove = false) {
    const {id} = rowIdentity(row);
    return run(async write => {
      const cart = await currentCart();
      const lines = linesFor(cart, id);
      if (!lines.length) throw new Error('Your cart changed. Please review the refreshed quantities.');
      if (lines.some(line => remove ? line.instructions?.can_remove === false : line.instructions?.can_update_quantity === false)) throw new Error('This item must be updated with its parent item.');
      const current = totalQuantity(lines), wanted = remove ? 0 : Math.max(1, current + delta);
      if (wanted === current) return cart;
      let result;
      if (remove && lines.length > 1) result = await write('update', {updates:Object.fromEntries(lines.map(line => [line.key, 0]))});
      else {
        const line = lines.find(line => Number(line.final_line_price) > 0) || lines[0];
        result = await write('change', {id:line.key, quantity:remove ? 0 : Number(line.quantity) + delta});
      }
      if (totalQuantity(linesFor(result, id)) !== wanted) throw new Error('Cart quantities changed. Review the updated items before continuing.');
      return result;
    }, row);
  }
  function closeEditor(row) {
    const editor = q('[data-broek-editor]', row); if (editor) editor.hidden = true;
    const button = q('[data-broek-edit-toggle]', row); button?.setAttribute('aria-expanded', 'false'); button?.focus({preventScroll:true});
  }
  function openEditor(row) {
    const editor = q('[data-broek-editor]', row); if (!editor) return;
    if (!editor.hidden) { closeEditor(row); return; }
    const {lines} = rowIdentity(row);
    if (lines.some(isolated) || planId(lines[0]) !== '0') return;
    editor.dataset.broekOriginalSignature = signature(lines);
    const label = q('[data-broek-editor-label]', editor), quantity = totalQuantity(lines);
    if (label) label.textContent = quantity > 1 ? `Change options for all ${quantity} pieces` : 'Change options';
    const select = q('[data-broek-edit-variant]', editor);
    if (select) select.value = String(lines[0].variant_id);
    editor.hidden = false; q('[data-broek-edit-toggle]', row)?.setAttribute('aria-expanded', 'true'); select?.focus({preventScroll:true});
  }
  async function editGroup(row) {
    const {id, lines:snapshot} = rowIdentity(row);
    const editor = q('[data-broek-editor]', row), select = q('[data-broek-edit-variant]', editor);
    const option = select?.selectedOptions[0];
    const variant = Number(option?.value);
    if (!option || option.disabled || !Number.isSafeInteger(variant) || variant <= 0) throw new Error('Choose an available size.');
    if (String(variant) === String(snapshot[0].variant_id)) { closeEditor(row); return; }
    return run(async (write, state) => {
      const before = await currentCart(), source = linesFor(before, id);
      if (!source.length || signature(source) !== editor.dataset.broekOriginalSignature) throw new Error('Your cart changed. Choose the new size again.');
      if (source.some(isolated) || source.some(line => planId(line) !== '0')) throw new Error('This item must be edited separately.');
      const additions = new Map();
      source.forEach(line => {
        const item = {id:variant, variant_id:variant, quantity:Number(line.quantity), properties:properties(line.properties)};
        const key = fullId(item);
        if (additions.has(key)) additions.get(key).quantity += item.quantity;
        else additions.set(key, item);
      });
      const beforeTotals = packTotals(before.items);
      state.review = cart => {
        const totals = packTotals(cart.items || []);
        return linesFor(cart, id).length > 0 && Array.from(additions.keys()).some(key => (totals.get(key) || 0) > (beforeTotals.get(key) || 0));
      };
      // Add with Shopify inventory validation first. Preserve every full property set.
      await write('add', {items:Array.from(additions.values()).map(({id, quantity, properties}) => ({id, quantity, properties}))});
      const added = await currentCart(), afterTotals = packTotals(added.items);
      if (signature(linesFor(added, id)) !== signature(source) || Array.from(additions).some(([key, item]) => (afterTotals.get(key) || 0) !== (beforeTotals.get(key) || 0) + item.quantity)) throw new Error('Size change interrupted. Please check your cart.');
      const originalLines = linesFor(added, id);
      // Discount allocation can change all line keys after the add, so use this fresh read.
      const result = await write('update', {updates:Object.fromEntries(originalLines.map(line => [line.key, 0]))});
      const finalTotals = packTotals(result.items || []);
      if (linesFor(result, id).length || Array.from(additions).some(([key, item]) => (finalTotals.get(key) || 0) !== (beforeTotals.get(key) || 0) + item.quantity)) throw new Error('Size change interrupted. Please check your cart.');
      return result;
    }, row);
  }
  function closeAddon(panel) {
    if (!panel) return; panel.classList.remove('is-open');
    const button = qa('.mini_cart_addon_btn', q(DRAWER)).find(el => el.dataset.open === panel.id);
    button?.classList.remove('open'); button?.focus({preventScroll:true});
  }
  function closeDrawer() {
    const drawer = q(DRAWER);
    if (typeof drawer?.close === 'function') drawer.close();
    else { drawer?.classList.remove('open'); q('.minicart__wrapper', drawer)?.classList.remove('open'); document.documentElement.classList.remove('open-minicart', 'open-drawer'); }
    if (opener?.isConnected) opener.focus({preventScroll:true});
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('.minicart__action,.header__minicart a'); if (trigger && !trigger.closest(DRAWER)) opener = trigger;
    if (busy && event.target.closest('.product-form__submit,button[name="add"],minicart-item-edit')) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    const retry = event.target.closest(`${DRAWER} [data-broek-cart-retry]`);
    if (retry) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!busy) run(async () => currentCart()).catch(() => {});
      return;
    }
    const actionButton = event.target.closest(`${DRAWER} [data-broek-qty-action],${DRAWER} mini-cart-remove-button,${DRAWER} [data-broek-edit-toggle],${DRAWER} [data-broek-edit-save],${DRAWER} [data-broek-edit-cancel]`);
    if (actionButton) {
      event.preventDefault(); event.stopImmediatePropagation(); if (busy) return;
      const row = actionButton.closest('[data-broek-line-key]'); if (!row) return;
      try {
        if (actionButton.hasAttribute('data-broek-edit-toggle')) openEditor(row);
        else if (actionButton.hasAttribute('data-broek-edit-cancel')) closeEditor(row);
        else if (actionButton.hasAttribute('data-broek-edit-save')) editGroup(row).catch(error => { if (!q('[data-broek-cart-status]', q(DRAWER))?.textContent) status(error.message); });
        else changeGroup(row, actionButton.dataset.broekQtyAction === 'plus' ? 1 : -1, actionButton.matches('mini-cart-remove-button')).catch(() => {});
      } catch (error) { status(error.message); }
      return;
    }
    const addonButton = event.target.closest(`${DRAWER} .mini_cart_addon_btn[data-open]`);
    if (addonButton) {
      event.preventDefault(); event.stopImmediatePropagation();
      const panel = document.getElementById(addonButton.dataset.open); if (!panel) return;
      const open = !panel.classList.contains('is-open');
      qa('.cart__addon-content.is-open', q(DRAWER)).forEach(el => el.classList.remove('is-open'));
      qa('.mini_cart_addon_btn.open', q(DRAWER)).forEach(el => el.classList.remove('open'));
      panel.classList.toggle('is-open', open); addonButton.classList.toggle('open', open);
      if (open) q('input,textarea,select', panel)?.focus({preventScroll:true}); return;
    }
    const cancel = event.target.closest(`${DRAWER} .cart__addon-content .btn-cancel`);
    if (cancel) { event.preventDefault(); event.stopImmediatePropagation(); closeAddon(cancel.closest('.cart__addon-content')); return; }
    const noteSave = event.target.closest(`${DRAWER} .btn-save[data-action="note"]`);
    if (noteSave) {
      event.preventDefault(); event.stopImmediatePropagation(); if (busy) return;
      const note = q('.cart-note', q(DRAWER))?.value || '';
      mutateCart('update', {note}).then(() => closeAddon(q('#note', q(DRAWER)))).catch(() => {}); return;
    }
    const close = event.target.closest(`${DRAWER} .close-cart-button,${DRAWER} .cart-empty .close-cart`);
    if (close) { event.preventDefault(); event.stopImmediatePropagation(); closeDrawer(); }
  }, true);
  document.addEventListener('submit', event => {
    if (((busy || uncertain || needsReview) && event.target?.id === 'minicart') || (busy && event.target instanceof HTMLFormElement && event.target.action.includes('/cart/add'))) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('keydown', event => {
    const drawer = q(DRAWER);
    if (!drawer?.classList.contains('open') || document.querySelector('.tingle-modal.tingle-modal--visible')) return;
    if (event.key === 'Escape') {
      const editor = event.target instanceof Element ? event.target.closest('[data-broek-editor]:not([hidden])') : null;
      event.preventDefault(); event.stopImmediatePropagation();
      if (editor) closeEditor(editor.closest('[data-broek-line-key]'));
      else { const panel = q('.cart__addon-content.is-open', drawer); if (panel) closeAddon(panel); else closeDrawer(); }
      return;
    }
    if (event.key !== 'Tab') return;
    const scope = q('.cart__addon-content.is-open', drawer) || drawer;
    const controls = qa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea,[tabindex="0"]', scope).filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[hidden]') && (!el.closest('.cart__addon-content') || el.closest('.cart__addon-content').classList.contains('is-open')));
    const first = controls[0], last = controls.at(-1);
    if (!first) { event.preventDefault(); drawer.focus(); return; }
    if (!scope.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
  }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted && q(DRAWER) && !busy) run(async () => currentCart()).catch(() => {});
  });
  window.BroekCartUX = {version:'qa-06sep-groups-r3', applyResponse, changeLine, updateCart:payload => mutateCart('update', payload), isBusy:() => busy, refreshFromServer, currentCart, sync:syncGroups, normalizeDuplicates:async cart => cart};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncGroups, {once:true});
  else syncGroups();
})();
